// Mirror of core/src/services/pricingConfigService.js → computeCosts + DEFAULTS.
// Kept byte-for-byte equivalent in math so the dashboard can recompute the cost
// breakdown live (edit-as-you-type) without a round-trip, and the saved result
// matches the server's `computed`. If you change the formula on one side, change
// it on the other.

export const DEFAULTS = Object.freeze({
  deepgramSttPerMin: 0.0077,
  auraTtsPer1kChars: 0.03,
  elevenLabsPer1kChars: 0.05,
  claudeInputPerMtok: 3.0,
  claudeOutputPerMtok: 15.0,
  livekitPerMin: 0.0,
  charsPerMinute: 900,
  aiTalkTimePercent: 50,
  claudeInputTokensPerMin: 2500,
  claudeOutputTokensPerMin: 700,
  ttsVendor: 'elevenlabs',
  hetznerMonthly: 36.0,
  railwayMonthly: 20.0,
  netlifyMonthly: 20.0,
  assumedMonthlyMinutes: 5000,
  markupPercent: 300
});

const amortize = (monthly, minutes) => (minutes > 0 ? monthly / minutes : 0);

export function computeCosts(input) {
  const c = { ...DEFAULTS, ...(input || {}) };

  const sttPerMin = c.deepgramSttPerMin; // runs the whole call — not scaled

  // Share of the minute the AI actually speaks (caller talks + silence the rest).
  // Scales AI-generated work (TTS audio + Claude OUTPUT tokens) down from the
  // worst-case full-minute-speaking assumption. STT/Claude INPUT/infra run the
  // whole call → not scaled.
  const talkTimeFactor = Math.min(Math.max(c.aiTalkTimePercent ?? 100, 0), 100) / 100;

  const ttsRatePer1k =
    c.ttsVendor === 'aura' ? c.auraTtsPer1kChars : c.elevenLabsPer1kChars;
  const ttsPerMin = ((c.charsPerMinute * talkTimeFactor) / 1000) * ttsRatePer1k;

  const claudeInputPerMin = (c.claudeInputTokensPerMin / 1_000_000) * c.claudeInputPerMtok;
  const claudeOutputPerMin = ((c.claudeOutputTokensPerMin * talkTimeFactor) / 1_000_000) * c.claudeOutputPerMtok;
  const claudePerMin = claudeInputPerMin + claudeOutputPerMin;

  const livekitPerMin = c.livekitPerMin;

  const variablePerMin = sttPerMin + ttsPerMin + claudePerMin + livekitPerMin;

  const fixedMonthly = c.hetznerMonthly + c.railwayMonthly + c.netlifyMonthly;
  const fixedPerMin = c.assumedMonthlyMinutes > 0 ? fixedMonthly / c.assumedMonthlyMinutes : 0;

  const costPerMin = variablePerMin + fixedPerMin;

  const breakdown = [
    { key: 'stt', label: 'Deepgram STT (nova-3)', kind: 'variable', perMin: sttPerMin },
    {
      key: 'tts',
      label: c.ttsVendor === 'aura' ? 'Deepgram Aura-2 TTS' : 'ElevenLabs Flash TTS',
      kind: 'variable',
      perMin: ttsPerMin
    },
    { key: 'claudeInput', label: 'Claude input tokens', kind: 'variable', perMin: claudeInputPerMin },
    { key: 'claudeOutput', label: 'Claude output tokens', kind: 'variable', perMin: claudeOutputPerMin },
    { key: 'livekit', label: 'LiveKit (self-hosted on the box)', kind: 'variable', perMin: livekitPerMin },
    { key: 'hetzner', label: 'Hetzner VPS (the box)', kind: 'fixed', perMin: amortize(c.hetznerMonthly, c.assumedMonthlyMinutes) },
    { key: 'railway', label: 'Railway (core service)', kind: 'fixed', perMin: amortize(c.railwayMonthly, c.assumedMonthlyMinutes) },
    { key: 'netlify', label: 'Netlify (dashboard)', kind: 'fixed', perMin: amortize(c.netlifyMonthly, c.assumedMonthlyMinutes) }
  ];

  const markupMultiplier = 1 + c.markupPercent / 100;
  const pricePerMin = costPerMin * markupMultiplier;
  const marginPerMin = pricePerMin - costPerMin;

  const packages = [500, 2000, 10000].map((minutes) => {
    const price = pricePerMin * minutes;
    const cost = costPerMin * minutes;
    return {
      minutes,
      price,
      cost,
      margin: price - cost,
      marginPercent: price > 0 ? ((price - cost) / price) * 100 : 0
    };
  });

  return {
    variablePerMin,
    fixedPerMin,
    fixedMonthly,
    costPerMin,
    pricePerMin,
    marginPerMin,
    marginPercent: pricePerMin > 0 ? (marginPerMin / pricePerMin) * 100 : 0,
    markupMultiplier,
    breakdown,
    packages
  };
}
